/** Exec-only "Custom Metrics" panel (CONF-212, CONF-258). Owns NO permission
 *  logic — the card decides whether to mount it, and that gate is purely UI:
 *  only an execAccess user sees this panel to CREATE overrides. It is not the
 *  whole story for who can HOLD them — a quote saved with overrides hydrates
 *  them for every user who loads it (useExecMetrics.hydrate runs
 *  unconditionally), so a non-exec viewer sees the same Current/Custom pairs
 *  on the System Attributes card with no panel of their own, and any
 *  artifact they generate from it is confirmed and audited the same way
 *  (I4). Session-only: overrides live in useExecMetrics and are lost on
 *  refresh unless the quote is saved.
 *  Rows are the six METRIC_OVERRIDE_KEYS per-node throughput entries,
 *  grouped All-Flash / Mixed Fleet under a collapsible header (same ▾/▸
 *  pattern as the admin catalog grid). Collapse state is component-local
 *  and session-only like the overrides; a disabled set renders no group.
 *  Current is each node's raw throughput rating — before write efficiency
 *  and before MLEC (see domain/MetricsOverrides.ts) — read straight off
 *  baselineMetrics.throughputBasis, never from a display metric key. Custom
 *  is the exec's input in the same per-node figure. */
import React, { useState } from 'react';
import { METRIC_OVERRIDE_KEYS, toDisplay } from '../domain/MetricsOverrides';
import { getThroughputUnit } from '../utils/configUtilities';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

const parsePositive = (raw) => {
  const n = parseFloat(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const roundedDisplayStr = (v) => (v == null ? '' : String(Math.round(v * 10) / 10));

function MetricRow({ meta, rawRating, override, isPBUnit, stale, onCommit, onClear }) {
  const unit = getThroughputUnit(isPBUnit);
  const baseline = toDisplay(rawRating, isPBUnit);
  const customDisplay = override ? toDisplay(override.value, isPBUnit) : null;
  const [draft, setDraft] = useState(roundedDisplayStr(customDisplay));
  const [lastShown, setLastShown] = useState(customDisplay);
  // Review Focus 1 follow-up: true only once the user has actually typed into
  // this field since it was last (re)populated — a blur with this false must
  // never commit or clear. Without it, merely tabbing through a row re-commits
  // the ROUNDED draft (e.g. a saved 90.818 becomes 90.8) and silently
  // re-baselines the override, clearing a stale hint the exec never saw.
  const [touched, setTouched] = useState(false);
  if (customDisplay !== lastShown) {                     // external change (clear/reset/hydrate)
    setLastShown(customDisplay);
    setDraft(roundedDisplayStr(customDisplay));
    setTouched(false);
  }

  const commit = () => {
    if (!touched) return;                                // untouched: focus/blur alone changes nothing
    setTouched(false);
    if (draft.trim() === '') {                           // emptied: clear, never commit 0
      if (override) onClear(meta.key);
      return;
    }
    const n = parsePositive(draft);
    if (n === null) {                                    // junk: revert the field, change nothing
      setDraft(roundedDisplayStr(customDisplay));
      return;
    }
    onCommit(meta.key, n, baseline, isPBUnit);
  };

  return (
    <tr className={override ? 'bg-morganite/10' : ''}>
      <td className="px-2 py-0.5 text-sm">
        {meta.label}{meta.note && <span className="ml-1 text-xs text-gray-400">{meta.note}</span>}
      </td>
      <td className="px-2 py-0.5 text-sm text-gray-400">{fmt(baseline)} {unit}</td>
      <td className="px-2 py-0.5">
        <input
          type="text" inputMode="decimal" value={draft} aria-label={`${meta.label} custom value`}
          onChange={(e) => { setTouched(true); setDraft(e.target.value); }} onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="input-box w-28 py-1 text-sm text-right"
          placeholder={fmt(baseline)}
        />
        <span className="ml-1 text-xs text-gray-400">{unit}</span>
      </td>
      <td className="px-2 py-0.5 text-xs">
        {override && (
          <button type="button" onClick={() => onClear(meta.key)} className="underline text-gray-300 hover:text-white">
            clear
          </button>
        )}
        {override && stale && (
          <span className="ml-2 text-amber-400">config changed since override</span>
        )}
      </td>
    </tr>
  );
}

/** Display order and labels for the two metric sets. */
const METRIC_GROUPS = [
  { set: 'afa',    label: 'All-Flash',   enabled: (config) => Boolean(config.afaEnabled) },
  { set: 'hybrid', label: 'Mixed Fleet', enabled: (config) => Boolean(config.hybridEnabled) },
];

export default function ExecMetricsView({ execMetrics, baselineMetrics, config, staleKeys }) {
  const { overrides, changeCount, setMetric, clearMetric, resetAll } = execMetrics;
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (set) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(set)) next.delete(set); else next.add(set);
    return next;
  });
  const groups = METRIC_GROUPS
    .filter((g) => g.enabled(config))
    .map((g) => {
      const rows = METRIC_OVERRIDE_KEYS.filter((m) => m.set === g.set);
      const customCount = rows.filter((m) => overrides[m.key]).length;
      return { ...g, rows, customCount, open: !collapsed.has(g.set) };
    });

  return (
    <div id="exec-metrics-panel" className="mb-6 border border-morganite/40 rounded-lg p-4 text-white">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm">
          Custom per-node values feed the system throughput totals printed on every customer artifact. Price and BOM are unaffected.
        </p>
        <div className="flex items-center gap-3 text-sm">
          <span>{changeCount} metric override{changeCount === 1 ? '' : 's'}</span>
          <button type="button" onClick={resetAll} disabled={changeCount === 0}
            className="px-2 py-1 rounded border border-gray-500 disabled:opacity-40">
            Reset all
          </button>
        </div>
      </div>
      <table>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
            <th className="px-2 py-1">Metric</th><th className="px-2 py-1">Current</th>
            <th className="px-2 py-1">Custom</th><th className="px-2 py-1" />
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.set}>
            <tr>
              <th scope="colgroup" colSpan={4} className="text-left px-2 pt-2 pb-0.5">
                <button
                  type="button" onClick={() => toggle(g.set)} aria-expanded={g.open}
                  className="text-morganite text-xs tracking-wide uppercase"
                >
                  {g.open ? '▾' : '▸'} {g.label} · {g.customCount} of {g.rows.length} custom
                </button>
              </th>
            </tr>
            {g.open && g.rows.map((meta) => (
              <MetricRow key={meta.key} meta={meta}
                rawRating={Number(baselineMetrics?.throughputBasis?.[meta.key]?.rating ?? 0)}
                override={overrides[meta.key]} isPBUnit={config.isPBUnit}
                stale={staleKeys?.has(meta.key) ?? false}
                onCommit={setMetric} onClear={clearMetric} />
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
